import { Component, OnInit, Input, forwardRef, Injector, ElementRef, HostListener, ViewChildren, QueryList } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NgControl } from '@angular/forms';
import {
  isValid,
  format,
  startOfMonth,
  endOfMonth,
  isAfter,
  isBefore,
  isEqual,
  getDaysInMonth,
  parseISO
} from 'date-fns';
import { enCA, frCA } from 'date-fns/locale';

let counter = 0;

@Component({
    selector: 'dip-registration-full-date',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DipRegistrationFullDateComponent),
            multi: true
        }
    ],
    template: `
    <div class="col-sm-12">
      <label *ngIf="label" [for]="'year-' + id">{{ label }}</label>
    </div>

    <div class="col-sm-3 form-group mb-20">
      <label [for]="'year-' + id" class="sr-only">{{ "DIPRSRegistration.customerIdentification.dateOfBirth.year.placeHolder" | translate }}</label>
      <div class="td-select">
        <select class="form-control"
          (change)="onYearChange()"
          [(ngModel)]="year"
          [disabled]="yearDisabled"
          [id]="'year-' + id"
          [class.is-invalid]="ngControl.touched && ngControl.invalid"
          [attr.aria-invalid]="ngControl.touched && ngControl.invalid"
          #relatedItem
          >
          <option value="" [disabled]="year">{{ 'DIPRSRegistration.customerIdentification.dateOfBirth.year.placeHolder' | translate }}</option>
          <option *ngFor="let year of years" [ngValue]="year">{{ year }}</option>
        </select>
      </div>
    </div>

    <div class="col-sm-4 form-group mb-20">
      <label [for]="'month-' + id" class="sr-only">{{ "DIPRSRegistration.customerIdentification.dateOfBirth.month.placeHolder" | translate }}</label>
      <div class="td-select">
        <select class="form-control"
          (change)="onMonthChange()"
          [(ngModel)]="month"
          [disabled]="monthDisabled"
          [id]="'month-' + id"
          [class.is-invalid]="ngControl.touched && ngControl.invalid"
          [attr.aria-invalid]="ngControl.touched && ngControl.invalid"
          #relatedItem
          >
          <option value="" [disabled]="month">{{ 'DIPRSRegistration.customerIdentification.dateOfBirth.month.placeHolder' | translate }}</option>
          <option *ngFor="let month of months" [ngValue]="month">{{ formatMonth(month) }}</option>
        </select>
      </div>
    </div>

    <div class="col-sm-3 form-group">
      <label [for]="'day-' + id" class="sr-only">{{ "DIPRSRegistration.customerIdentification.dateOfBirth.day.placeHolder" | translate }}</label>
      <div class="td-select">
        <select class="form-control"
          (change)="onDayChange()"
          [(ngModel)]="day"
          [disabled]="dayDisabled"
          [id]="'day-' + id"
          [class.is-invalid]="ngControl.touched && ngControl.invalid"
          [attr.aria-invalid]="ngControl.touched && ngControl.invalid"
          #relatedItem
          >
          <option value="" [disabled]="day">{{ 'DIPRSRegistration.customerIdentification.dateOfBirth.day.placeHolder' | translate }}</option>
          <option *ngFor="let day of days" [ngValue]="day">{{ day }}</option>
        </select>
      </div>
    </div>
  `,
    standalone: false
})
export class DipRegistrationFullDateComponent implements OnInit, ControlValueAccessor {
  @Input() language: string;
  @Input() minDate: string;
  @Input() maxDate: string;
  @Input() label: string;

  years = new Array<string>();
  months = new Array<string>();
  days = new Array<string>();

  year = '';
  month = '';
  day = '';

  yearDisabled = false;
  monthDisabled = true;
  dayDisabled = true;

  id = counter++;
  ngControl: NgControl;

  private onChange: (value: string) => void;
  private onTouched: () => void;

  @ViewChildren('relatedItem') relatedItems: QueryList<ElementRef<HTMLSelectElement>>;

  @HostListener('focusout', ['$event']) onHostFocusout(event: FocusEvent) {
    if (!this.ngControl.touched && !this.relatedItems.find(relatedItem => relatedItem.nativeElement === event.relatedTarget)) {
      this.onTouched();
    }
  }

  constructor(
    private injector: Injector,
    private elementRef: ElementRef
  ) {
  }
  ngOnInit() {
    this.ngControl = this.injector.get(NgControl);

    const min = parseISO(this.minDate).getFullYear();
    const max = parseISO(this.maxDate).getFullYear();

    for (let i = 0; i <= max - min; i += 1) {
      this.years.unshift(String(min + i));
    }
  }

  onYearChange() {
    this.month = '';
    this.day = '';
    this.monthDisabled = false;
    this.dayDisabled = true;
    this.onChange(null);

    this.months.splice(0);
    const min = startOfMonth(parseISO(this.minDate));
    const max = endOfMonth(parseISO(this.maxDate));

    for (let i = 0; i < 12; i += 1) {
      const date = new Date(Number(this.year), i, 1);
      if (isEqual(date, min) || (isAfter(date, min) && isBefore(date, max))) {
        this.months.push(String(date.getMonth()));
      }
    }
  }

  onMonthChange() {
    this.day = '';
    this.dayDisabled = false;
    this.onChange(null);

    this.days.splice(0);
    const min = parseISO(this.minDate);
    const max = parseISO(this.maxDate);
    const monthDate = new Date(Number(this.year), Number(this.month), 1);

    for (let i = 1; i <= getDaysInMonth(monthDate); i += 1) {
      const date = new Date(Number(this.year), Number(this.month), i);
      if (isEqual(date, min) || isEqual(date, max) || (isAfter(date, min) && isBefore(date, max))) {
        this.days.push(String(i));
      }
    }
  }

  onDayChange() {
    const value = format(new Date(Number(this.year), Number(this.month), Number(this.day)), 'yyyy-MM-dd');
    this.onChange(value);
  }

  formatMonth(value: string) {
    const date = new Date();
    date.setDate(1);
    date.setMonth(Number(value));
    return format(date, 'MMMM', { locale: this.language === 'fr' ? frCA : enCA });
  }

  writeValue(value: string): void {
    const date = parseISO(value);
    if (isValid(date)) {
      this.year = String(date.getFullYear());
      this.month = String(date.getMonth());
      this.day = String(date.getDate());
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
}
